import PropTypes from "prop-types";
import styles from "./VerifyPopUp.module.css";

import Header from "./Verify_Header.svg";
import Phone from "./phone.svg";
import Email from "./email.svg";
import VerifiedIcon from "./Verified_Icon.svg";
import Loader from "components/Loader";

import { Button } from "../components/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { useAccount } from "utils/AccountInfoContext";
import { usePersonal } from "PersonalPage/usePersonal";
import { useVerifyPopUp } from "./useVerifyPopUp";
import { useTranslation } from "react-i18next";

import parsePhoneNumber, { AsYouType } from "libphonenumber-js";
import environment from "../environment";

/**
 * Opens a pop up to verify phone numbers/emails
 */

export function VerifyPopUp({}) {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    accountData,
    activeUser,
    issuesData,
    isIssuesLoading,
    refetchAccountData,
    refetchIssuesData,
  } = useAccount();
  const { updatePhone, updateEmail } = usePersonal();
  const { phoneSendPost, emailSendPost, phoneVerifyPost, emailVerifyPost } =
    useVerifyPopUp();

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [phoneVerified, setPhoneVerified] = useState(true);
  const [emailVerified, setEmailVerified] = useState(true);
  const [verificationFailed, setVerificationFailed] = useState(false);

  const [phoneMissing, setPhoneMissing] = useState(false);
  const [emailMissing, setEmailMissing] = useState(false);

  const [phoneCode, setPhoneCode] = useState("111111");
  const [emailCode, setEmailCode] = useState("454545");

  const [editPhone, setEditPhone] = useState(false);
  const [editEmail, setEditEmail] = useState(false);

  const [phoneInput, setPhoneInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emailIsValid, setEmailIsValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  const [verifyBtnDisabled, setVerifyBtnDisabled] = useState(true);

  const [loading, setLoading] = useState(isIssuesLoading);

  const WEB_APP_COUNTRY = environment?.WEB_APP_COUNTRY;

  useEffect(() => {
    setLoading(isIssuesLoading);
  }, [isIssuesLoading]);

  useEffect(() => {
    if (issuesData) {
      setPhoneVerified(true);
      setEmailVerified(true);
      setPhoneMissing(false);
      setEmailMissing(false);

      issuesData.map((issue, index) => {
        if (issue.Type == "phone-unverified") {
          setPhoneVerified(false);
        } else if (issue.Type == "account-primary-phone-missing") {
          setPhoneMissing(true);
        }
        if (issue.Type == "email-unverified") {
          setEmailVerified(false);
        } else if (issue.Type == "account-primary-email-missing") {
          setEmailMissing(true);
        }
      });
    }

    if (activeUser.phone) {
      setPhone(
        `${activeUser.phone.slice(0, 3)}.${activeUser.phone.slice(
          3,
          6
        )}.${activeUser.phone.slice(6)}`
      );

      setPhoneInput(activeUser.phone);
    } else {
      setPhone(""); // Ensure phone is empty when missing
      setPhoneMissing(true); // Mark phone as missing so the "Add" button shows
    }

    if (activeUser.email) {
      setEmail(activeUser.email);

      setEmailInput(activeUser.email);
    } else {
      setEmail("");
      setEmailMissing(true);
    }
  }, [activeUser, issuesData]);

  const [inputStates, setInputStates] = useState([
    { digit: "", id: 1 },
    { digit: "", id: 2 },
    { digit: "", id: 3 },
    { digit: "", id: 4 },
    { digit: "", id: 5 },
    { digit: "", id: 6 },
  ]);

  const [inputEmailStates, setInputEmailStates] = useState([
    { digit: "", id: 1 },
    { digit: "", id: 2 },
    { digit: "", id: 3 },
    { digit: "", id: 4 },
    { digit: "", id: 5 },
    { digit: "", id: 6 },
  ]);

  const phoneCodeRefs = useRef(Array(6).fill(null));
  const emailCodeRefs = useRef(Array(6).fill(null));

  useEffect(() => {
    const areDigitsFilled = (states) => {
      return states.every((state) => state.digit !== "");
    };
    const phoneDigitsFilled = areDigitsFilled(inputStates);

    const emailDigitsFilled = areDigitsFilled(inputEmailStates);

    setVerifyBtnDisabled(!(phoneDigitsFilled || emailDigitsFilled));
  }, [inputStates, inputEmailStates]);

  const handleCancelClick = () => {
    setEditEmail(false);
    setEditPhone(false);
    setPhoneInput(activeUser.phone);
    setEmailInput(activeUser.email);
  };

  const handleSaveClicked = async (type) => {
    setIsEmailValid(emailIsValid);
    if (emailIsValid) {
      setLoading(true);
      if (type == "Phone") {
        let parse = parsePhoneNumber(phoneInput, environment.WEB_APP_COUNTRY);

        try {
          await updatePhone.mutateAsync({
            contactId: activeUser.contactId,
            phoneData: { phone: parse.nationalNumber },
          });
          refetchIssuesData();
        } catch (error) {
          console.error(t("Failed to update phone"), error);
        }
        setEditPhone(false);
      } else {
        try {
          await updateEmail.mutateAsync({
            contactId: activeUser.contactId,
            email: emailInput,
          });
          refetchIssuesData();
        } catch (error) {
          console.error(t("Failed to update email"), error);
        }
        setEditEmail(false);
      }
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let newValue = e.target.value;

    if (editPhone) {
      let input = new AsYouType(environment.WEB_APP_COUNTRY).input(newValue);
      let parse = parsePhoneNumber(input, environment.WEB_APP_COUNTRY);

      setEmailIsValid(parse?.isPossible(input) ? true : false);

      setPhoneInput(input);
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailIsValid(emailPattern.test(newValue));

      setEmailInput(newValue);
    }
  };

  const onVerifyPhone = async () => {
    setLoading(true);
    let code = "";

    inputStates.map((state, index) => {
      return (code += state.digit);
    });

    try {
      let response = await phoneVerifyPost.mutateAsync({
        contactId: activeUser.contactId,
        verifyData: {
          code: code,
          phone: phone.replace(/\D/g, ""),
        },
      });
      if (response == "True") {
        setShowPhoneConfirmation(false);
        refetchAccountData();
        refetchIssuesData();
        setVerificationFailed(false);
        setPhoneVerified(true);
      } else {
        resetVerificationForm();
        setVerificationFailed(true);
      }
    } catch (error) {
      console.error(t("Failed to verify phone code"), error);
    }
    setLoading(false);
  };

  const onVerifyEmail = async () => {
    setLoading(true);
    let code = "";

    inputEmailStates.map((state, index) => {
      return (code += state.digit);
    });

    try {
      let response = await emailVerifyPost.mutateAsync({
        contactId: activeUser.contactId,
        verifyData: {
          code: code,
          email: email,
        },
      });
      if (response == "True") {
        setShowEmailConfirmation(false);
        refetchAccountData();
        refetchIssuesData();
        setVerificationFailed(false);
        setEmailVerified(true);
      } else {
        resetVerificationForm();
        setVerificationFailed(true);
      }
    } catch (error) {
      console.error(t("Failed to verify email code"), error);
    }
    setLoading(false);
  };

  const onResend = (type) => {
    if (type == "Phone") {
      handleVerifyPhoneNumber();
    } else {
      handleVerifyEmail();
    }
  };

  const handleCodeInputStateChange = (index, value, type) => {
    let newStates =
      type === "Phone" || type === "Code"
        ? [...inputStates]
        : [...inputEmailStates];

    // If the input is empty (backspace), set the digit to an empty string
    if (value === "") {
      newStates[index].digit = "";
    } else if (!isNaN(value)) {
      newStates[index].digit = parseInt(value);
    }

    if (type === "Code") {
      setInputStates(newStates);
    } else if (type === "Phone") {
      let formatInput = new AsYouType(environment.WEB_APP_COUNTRY).input(value);
      setInputStates(formatInput);
    } else {
      setInputEmailStates(newStates);
    }

    // Move focus to the next input using refs to avoid stale DOM reads
    if (value !== "" && index < 5) {
      const refs = type === "Code" ? phoneCodeRefs : emailCodeRefs;
      refs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, event, type) => {
    if (event.key === "Backspace" && index > 0) {
      const states = type === "Code" ? inputStates : inputEmailStates;
      if (states[index].digit === "") {
        const refs = type === "Code" ? phoneCodeRefs : emailCodeRefs;
        refs.current[index - 1]?.focus();
      }
    }
  };

  const resetVerificationForm = () => {
    setInputStates([
      { digit: "", id: 1 },
      { digit: "", id: 2 },
      { digit: "", id: 3 },
      { digit: "", id: 4 },
      { digit: "", id: 5 },
      { digit: "", id: 6 },
    ]);

    setInputEmailStates([
      { digit: "", id: 1 },
      { digit: "", id: 2 },
      { digit: "", id: 3 },
      { digit: "", id: 4 },
      { digit: "", id: 5 },
      { digit: "", id: 6 },
    ]);
  };

  const goBackToReferrerIfExists = async () => {
    let _prevUrl = document.referrer;

    if (
      _prevUrl &&
      !_prevUrl.includes("settings") &&
      !_prevUrl.includes("apps-dev.wwdb.com") &&
      !_prevUrl.includes("apps-staging.wwdb.com") &&
      !_prevUrl.includes("wwdb.com")
    ) {
      window.location.assign(_prevUrl);
    } else {
      history.push("/");
    }
  };

  const handleVerifyPhoneNumber = async () => {
    setLoading(true);
    try {
      await phoneSendPost.mutateAsync({
        contactId: activeUser.contactId,
        phoneData: { phone: phone.replace(/\D/g, "") },
      });
    } catch (error) {
      console.error(t("Failed to send phone code"), error);
    }
    setShowPhoneConfirmation(true);
    setLoading(false);
  };
  const handleVerifyEmail = async () => {
    setLoading(true);
    try {
      await emailSendPost.mutateAsync({
        contactId: activeUser.contactId,
        emailData: { email: email },
      });
    } catch (error) {
      console.error(t("Failed to send email code"), error);
    }
    setShowEmailConfirmation(true);
    setLoading(false);
  };

  const handleGoBack = async () => {
    if (showPhoneConfirmation || showEmailConfirmation) {
      setShowPhoneConfirmation(false);
      setShowEmailConfirmation(false);
    } else {
      await goBackToReferrerIfExists();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.btnHeader}>
            <Button
              className={styles.btnBack}
              buttonText={t("Back")}
              onClick={() => handleGoBack()}
            />
            <Button
              className={styles.btnSkip}
              buttonText={t("Skip")}
              onClick={() => handleGoBack()}
            />
          </div>
          {loading ? (
            <div>
              <Loader color={"#fff"} size={100} />
            </div>
          ) : (
            <>
              <div className={styles.image}>
                <img src={Header} alt="Verify Icon" />
              </div>
              <div className={styles.header}>{t("Let’s Verify")}</div>
              <div className={styles.message}>
                {t("Please take a moment to verify")}
              </div>
              <div className={styles.subMessage}>
                {t("This helps us confirm identity")}
              </div>
              {!showPhoneConfirmation && !showEmailConfirmation && (
                <div className={styles.middleContainer}>
                  <div className={styles.middleHeader}>
                    <img src={Phone} alt="Phone Icon" />{" "}
                    <span className={styles.marginLeft}>
                      {t("Phone Number")}
                    </span>
                  </div>
                  {!phoneVerified || phoneMissing ? (
                    <div className={styles.editBar}>
                      {editPhone ? (
                        <div className={styles.inputContainer}>
                          <input
                            id={"phone"}
                            className={
                              isEmailValid
                                ? styles.editInput
                                : styles.editInputInvalid
                            }
                            type="text"
                            value={phoneInput}
                            onChange={handleInputChange}
                            placeholder={"Phone Number"}
                          />
                        </div>
                      ) : (
                        <div className={styles.editBarPhoneNumber}>{phone}</div>
                      )}
                      {editPhone ? (
                        <div className={styles.buttonBar}>
                          <div
                            className={styles.cancelBtn}
                            onClick={() => handleCancelClick()}
                          >
                            {t("Cancel")}
                          </div>
                          <div
                            className={styles.saveBtn}
                            onClick={() => handleSaveClicked("Phone")}
                          >
                            {t("Save")}
                          </div>
                        </div>
                      ) : (
                        <div
                          className={!phone ? styles.addBtn : styles.editBtn}
                          onClick={() => {
                            !email
                              ? history.push("/personal")
                              : setEditPhone(true);
                          }}
                        >
                          {!phone ? t("Add") : t("Edit")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className={styles.verifiedBar}>
                        <div className={styles.verifiedBarPhoneNumber}>
                          {phone}
                        </div>
                      </div>
                      <img
                        src={VerifiedIcon}
                        alt="Verify Icon"
                        className={styles.verifiedIcon}
                      />
                    </>
                  )}

                  {(!phoneVerified || phoneMissing) && (
                    <>
                      <div
                        onClick={phone ? () => handleVerifyPhoneNumber() : null}
                        className={!phone ? styles.disabled : styles.verifyBtn}
                      >
                        {t("Verify")}
                      </div>

                      <div className={styles.ratesMessage}>
                        {!phone
                          ? t("Please add a phone number to verify")
                          : t("Message and date rates")}
                      </div>
                    </>
                  )}
                </div>
              )}

              {showPhoneConfirmation && (
                <>
                  <div className={styles.middleContainer}>
                    <div className={styles.middleHeader}>
                      <img src={Phone} alt="Phone Icon" />{" "}
                      <span className={styles.marginLeft}>
                        {t("Phone Number")}
                      </span>
                    </div>

                    {!phoneVerified && (
                      <>
                        {verificationFailed ? (
                          <div className={styles.verificationFailedMessage}>
                            <div className={styles.verificationFailedIcon}>
                              <svg
                                width="30"
                                height="30"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3 19.5L12 5L21 19.5H3Z"
                                  fill="#FF0000"
                                  stroke="#FFFFFF"
                                  stroke-width="1"
                                  stroke-linejoin="round"
                                  stroke-linecap="round"
                                />
                                <circle
                                  cx="12"
                                  cy="17"
                                  r="1.5"
                                  fill="#FFFFFF"
                                />
                                <path
                                  d="M12 14V10"
                                  stroke="#FFFFFF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                            {t("Incorrect verification code")}
                          </div>
                        ) : (
                          <div className={styles.codeMessage}>
                            {t("Enter your code")}
                          </div>
                        )}
                      </>
                    )}
                    {!phoneVerified && (
                      <div className={styles.codeContainer}>
                        {inputStates.map((state, index) => {
                          return (
                            <input
                              ref={(el) => (phoneCodeRefs.current[index] = el)}
                              className={styles.input}
                              maxLength="1"
                              type="text"
                              inputMode="numeric"
                              value={state.digit}
                              onChange={(e) =>
                                handleCodeInputStateChange(
                                  index,
                                  e.target.value,
                                  "Code"
                                )
                              }
                              onKeyDown={(e) => handleCodeKeyDown(index, e, "Code")}
                              key={state.id}
                              pattern="\d*"
                            ></input>
                          );
                        })}
                      </div>
                    )}

                    {!phoneVerified && (
                      <div className={styles.sentMessage}>
                        {t("We sent a text message")} {phone}
                      </div>
                    )}

                    {!phoneVerified && (
                      <div
                        className={styles.resendMessage}
                        onClick={() => onResend("Phone")}
                      >
                        {t("Resend text")}{" "}
                        <FontAwesomeIcon icon={faSync} color="#ABD4DD" />
                      </div>
                    )}
                  </div>
                  {!phoneVerified && (
                    <Button
                      className={styles.btn}
                      buttonText={t("Verify")}
                      onClick={() => onVerifyPhone()}
                      disabled={verifyBtnDisabled}
                    />
                  )}
                </>
              )}

              {!showPhoneConfirmation && !showEmailConfirmation && (
                <>
                  <div className={styles.middleContainer}>
                    <div className={styles.middleHeader}>
                      <img src={Email} alt="Email Icon" />{" "}
                      <span className={styles.marginLeft}>
                        {t("Email Address")}
                      </span>
                    </div>

                    {(!emailVerified || emailMissing) && (
                      <div className={styles.editBar}>
                        {editEmail ? (
                          <div className={styles.inputContainer}>
                            <input
                              id={"email"}
                              className={
                                isEmailValid
                                  ? styles.editInput
                                  : styles.editInputInvalid
                              }
                              type="text"
                              value={emailInput}
                              onChange={handleInputChange}
                              placeholder={"Email"}
                            />
                          </div>
                        ) : (
                          <div className={styles.editBarEmail}>{email}</div>
                        )}
                        {editEmail ? (
                          <div className={styles.buttonBar}>
                            <div
                              className={styles.cancelBtn}
                              onClick={() => handleCancelClick()}
                            >
                              {t("Cancel")}
                            </div>
                            <div
                              className={styles.saveBtn}
                              onClick={() => handleSaveClicked("Email")}
                            >
                              {t("Save")}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div
                              className={
                                !email ? styles.addBtn : styles.editBtn
                              }
                              onClick={() => {
                                !email
                                  ? history.push("/personal")
                                  : setEditEmail(true);
                              }}
                            >
                              {!email ? t("Add") : t("Edit")}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {!emailVerified || emailMissing ? (
                      <>
                        <div
                          onClick={email ? () => handleVerifyEmail() : null}
                          className={
                            !email ? styles.disabled : styles.verifyBtn
                          }
                        >
                          {t("Verify")}
                        </div>

                        {!email && (
                          <div className={styles.ratesMessage}>
                            {t("Please add an email to verify")}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className={styles.verifiedBar}>
                          <div className={styles.verifiedBarPhoneNumebr}>
                            {email}
                          </div>
                        </div>
                        <img
                          src={VerifiedIcon}
                          alt="Verify Icon"
                          className={styles.verifiedIcon}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
              {showEmailConfirmation && (
                <>
                  <div className={styles.middleContainer}>
                    <div className={styles.middleHeader}>
                      <img src={Email} alt="Email Icon" />{" "}
                      <span className={styles.marginLeft}>
                        {t("Email Address")}
                      </span>
                    </div>

                    {!emailVerified && (
                      <>
                        {verificationFailed ? (
                          <div className={styles.verificationFailedMessage}>
                            <div className={styles.verificationFailedIcon}>
                              <svg
                                width="30"
                                height="30"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3 19.5L12 5L21 19.5H3Z"
                                  fill="#FF0000"
                                  stroke="#FFFFFF"
                                  stroke-width="1"
                                  stroke-linejoin="round"
                                  stroke-linecap="round"
                                />
                                <circle
                                  cx="12"
                                  cy="17"
                                  r="1.5"
                                  fill="#FFFFFF"
                                />
                                <path
                                  d="M12 14V10"
                                  stroke="#FFFFFF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                            {t("Incorrect verification code")}
                          </div>
                        ) : (
                          <div className={styles.codeMessage}>
                            {t("Enter your code")}
                          </div>
                        )}
                      </>
                    )}
                    {!emailVerified && (
                      <div className={styles.codeContainer}>
                        {inputEmailStates.map((state, index) => {
                          return (
                            <input
                              ref={(el) => (emailCodeRefs.current[index] = el)}
                              className={styles.input}
                              maxLength="1"
                              type="text"
                              inputMode="numeric"
                              value={state.digit}
                              onChange={(e) =>
                                handleCodeInputStateChange(
                                  index,
                                  e.target.value,
                                  "Email"
                                )
                              }
                              onKeyDown={(e) => handleCodeKeyDown(index, e, "Email")}
                              key={state.id}
                              pattern="\d*"
                            ></input>
                          );
                        })}
                      </div>
                    )}

                    {!emailVerified && (
                      <div className={styles.sentMessage}>
                        {t("We sent a email")} {email}
                      </div>
                    )}

                    {emailVerified && (
                      <div className={styles.phoneNumber}>{email}</div>
                    )}

                    {!emailVerified && (
                      <div
                        className={styles.resendMessage}
                        onClick={() => onResend("Email")}
                      >
                        {t("Resend email")}{" "}
                        <FontAwesomeIcon icon={faSync} color="#ABD4DD" />
                      </div>
                    )}
                  </div>
                  {!emailVerified && showEmailConfirmation && (
                    <Button
                      className={styles.btn}
                      buttonText={t("Verify")}
                      onClick={() => onVerifyEmail()}
                      disabled={verifyBtnDisabled}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

VerifyPopUp.propTypes = {
  /**
   * Function called to close pop up
   */
  onClose: PropTypes.func,
  /**
   * Bool to determine if pop up is opened
   */
  isOpen: PropTypes.bool,
  /**
   * Function called when verify is clicked
   */
  onVerify: PropTypes.func,
};
